import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link, useRouterState } from '@tanstack/react-router';
import { X } from 'lucide-react';
import type { JSX, RefObject } from 'react';
import { useOrdersAttentionCount } from '@/features/orders/use-attention-count.js';
import { cn } from '@/lib/cn.js';
import { NAV_ENTRIES } from './nav-config.js';

function isEntryActive(pathname: string, entryPath: string): boolean {
  return entryPath === '/' ? pathname === '/' : pathname.startsWith(entryPath);
}

/** Marque de l'application, partagée par la barre latérale, le tiroir et les écrans d'authentification. */
export function BrandMark({ className }: { className?: string }): JSX.Element {
  return (
    <span
      className={cn('flex items-center gap-2 text-sm font-semibold tracking-[-0.01em]', className)}
    >
      <span
        aria-hidden="true"
        className="flex size-6 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground"
      >
        F
      </span>
      Flux Miumlab
    </span>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: attentionCount } = useOrdersAttentionCount();

  return (
    <ul className="flex flex-col gap-0.5 px-2">
      {NAV_ENTRIES.map((entry) => {
        const Icon = entry.icon;
        const active = isEntryActive(pathname, entry.path);
        const badge = entry.badgeKey === 'orders-attention' ? attentionCount : undefined;
        return (
          <li key={entry.path}>
            <Link
              to={entry.path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'group flex h-9 items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-sidebar-muted transition-colors',
                'hover:bg-sidebar-accent/70 hover:text-sidebar-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                active && 'bg-sidebar-accent text-sidebar-foreground',
              )}
            >
              <Icon
                className={cn(
                  'size-4 shrink-0 transition-colors',
                  active
                    ? 'text-primary'
                    : 'text-sidebar-muted group-hover:text-sidebar-foreground',
                )}
                aria-hidden="true"
              />
              <span className="flex-1 truncate">{entry.label}</span>
              {badge ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-status-red-bg px-1.5 text-xs font-semibold tabular-nums text-status-red-fg">
                  {badge}
                </span>
              ) : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export interface SidebarProps {
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  /** Bouton qui ouvre le tiroir : le focus y revient à la fermeture (Échap, bouton, lien). */
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Barre latérale fixe de 232 px (`02` §0), repliée en tiroir sous 1024px —
 * écart assumé vis-à-vis de « non optimisé mobile » (`02` §0), demandé par le
 * client (voir `README-shell.md`). `lg:` (1024px) est le point de bascule
 * Tailwind par défaut, choisi pour coller exactement à cette exigence sans
 * configuration supplémentaire.
 *
 * Deux rendus du même contenu de navigation plutôt qu'un seul basculé en CSS :
 * la version bureau reste toujours montée (`hidden lg:flex`, pas de mise à
 * jour de layout au resize) : la version tiroir est un vrai dialogue Radix
 * (piège de focus, Échap, recouvrement) monté seulement à l'ouverture.
 */
export function Sidebar({
  mobileOpen,
  onMobileOpenChange,
  returnFocusRef,
}: SidebarProps): JSX.Element {
  return (
    <>
      <aside
        className="sticky top-0 hidden h-screen w-[232px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex"
        aria-label="Navigation principale"
      >
        <div className="flex h-12 shrink-0 items-center px-4">
          <BrandMark />
        </div>
        <nav className="flex-1 overflow-y-auto pb-4 pt-2">
          <NavList />
        </nav>
      </aside>

      <DialogPrimitive.Root open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-overlay data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out lg:hidden" />
          <DialogPrimitive.Content
            className="fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-overlay data-[state=open]:animate-slide-in-left data-[state=closed]:animate-slide-out-left lg:hidden"
            aria-label="Navigation principale"
            onCloseAutoFocus={(event) => {
              // Le tiroir est piloté (`open`), sans `Dialog.Trigger` : Radix ne sait pas
              // où rendre le focus, on le ramène explicitement sur le bouton de menu.
              if (returnFocusRef?.current) {
                event.preventDefault();
                returnFocusRef.current.focus();
              }
            }}
          >
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Barre de navigation principale de Flux Miumlab.
            </DialogPrimitive.Description>
            <div className="flex h-12 shrink-0 items-center justify-between pl-4 pr-2">
              <BrandMark />
              <DialogPrimitive.Close
                className="inline-flex size-9 items-center justify-center rounded-md text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Fermer la navigation"
              >
                <X className="size-4" aria-hidden="true" />
              </DialogPrimitive.Close>
            </div>
            <nav className="flex-1 overflow-y-auto pb-4 pt-2">
              <NavList
                onNavigate={() => {
                  onMobileOpenChange(false);
                }}
              />
            </nav>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
